import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	Grid,
	IconButton,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useCallback, useEffect, useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import { ExerciseColor, OnoffType, QualityType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import qs from 'qs';
import { GridColDef } from '@mui/x-data-grid';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import SetSingleOrderDialog from './components/SetSingleOrderDialog';
import {
	useDeleteWishMutation,
	useGetWishDetailQuery,
	useSetWishStatusMutation,
	useGetWishTreasurePageQuery,
} from '@/api/treasury/wishApiSlice';

import SquareIcon from '@mui/icons-material/Square';
import {
	GameBlessingCardTreasureDTO,
	TreasureFilter,
} from '@/api/DTO/treasury/wish.interface';
import WishInfoForm from './components/WishInfoForm';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTB } from '@/common/constants/common';
import AppImageView from '@/common/components/form-view/AppImageView';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function WishDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const [deleteWish] = useDeleteWishMutation();
	const { wishId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');
	const [setWishStatus] = useSetWishStatusMutation();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];

	const { data: details } = useGetWishDetailQuery(wishId || '', {
		refetchOnMountOrArgChange: true,
	});
	const defaultPropertyFilter: TreasureFilter = {
		pagination: initialPagination,
		sortings: [],
		seriesId: wishId || '',
	};

	const defaultValues: DefaultValues<TreasureFilter> = defaultPropertyFilter;
	const methods = useForm<TreasureFilter>({
		defaultValues,
	});

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as TreasureFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const transformedSearchParams: TreasureFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'seriesId',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetWishTreasurePageQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: GameBlessingCardTreasureDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	); // watch fields change

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		if (details?.data.published == OnoffType.on) {
			return enqueueSnackbar(t('snackbar.noDeleteMeditation'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('wishList.deleteChartlet')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('wishList.isDeleteWish')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteWish(wishId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteMeditationSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate('/treasury-manage/wish-list', { replace: true });
					}
				}}
			/>
		));
	};

	const handleSetStatus = async (type: number) => {
		const response = await setWishStatus({
			publishList: [{ id: wishId || '', published: type == 1 ? true : false }],
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('chartletList.treasureTitle'),
			flex: 25,
		},
		{
			field: 'quality',
			headerName: t('chartletList.quality'),
			flex: 25,
			renderCell: (params: any) => showLabel(params.row.quality, qualityList),
		},
		{
			field: 'costPoints',
			headerName: t('chartletList.integral'),
			flex: 25,
		},
		{
			field: 'updatedAt',
			headerName: t('chartletList.updateTime'),
			flex: 20,
		},
		{
			field: 'createdAt',
			headerName: t('chartletList.createTime'),
			flex: 20,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/treasury-manage/wish-list/info/${wishId}/detail/${params.row.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.wishDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('meditation.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('achievementList.seriesStatus')}
									valueElement={
										<PublishedChip
											published={details?.data.published}
											publishList={PUBLISHED_LISTB}
										/>
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('chartletList.seriesTitle')}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('chartletList.seriesColor')}
													valueElement={
														<Box sx={{ display: 'flex', alignItems: 'center' }}>
															<SquareIcon sx={{ color: details?.data.color }} />
															{details?.data.color}
														</Box>
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('chartletList.pushedTime')}
													value={
														!details?.data.publishedStartAt
															? '-'
															: dayjs(details?.data.publishedStartAt)
																	.locale('en')
																	.format(DISPLAY_DATETIME_12H_FORMAT)
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('chartletList.pulledTime')}
													value={
														!details?.data.publishedEndAt
															? '-'
															: dayjs(details?.data.publishedEndAt)
																	.locale('en')
																	.format(DISPLAY_DATETIME_12H_FORMAT)
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('chartletList.collectAllRewards')}
													valueElement={
														<Box sx={{ display: 'flex', alignItems: 'center' }}>
															{details?.data?.rewardDetail ? (
																<>
																	<PublishedChip
																		published={
																			details?.data?.rewardDetail?.published
																		}
																		publishList={PUBLISHED_LISTB}
																	/>
																	<div style={{ marginRight: '20px' }}></div>
																	<AppFieldView
																		label={''}
																		value={`${details?.data?.rewardDetail?.seriesTitleZh} - ${details?.data?.rewardDetail?.titleZh}`}
																	/>
																</>
															) : (
																'-'
															)}
														</Box>
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppImageView
													label={t('chartletList.seriesBgImage')}
													imageElement={
														details?.data.backgroundPic && (
															<img
																src={details?.data.backgroundPic.url}
																width={245}
																height={245}
																style={{
																	objectFit: 'contain',
																}}
															/>
														)
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppImageView
													label={t('galleryList.seriesImage')}
													imageElement={
														details?.data.completePic && (
															<img
																src={details?.data.completePic.url}
																width={245}
																height={245}
																style={{
																	objectFit: 'contain',
																}}
															/>
														)
													}
												/>
											</Grid>
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
										</Box>
										<WishInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>

				<Card sx={{ marginTop: 3 }}>
					<CardContent>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h6'>
								{t('chartletList.treasureList')}
							</Typography>
							<Stack direction={'row'} alignItems={'center'} spacing={3}>
								<SetSingleOrderDialog />
								<Button
									variant='contained'
									style={{ alignItems: 'center' }}
									endIcon={<NoteAltOutlinedIcon />}
									onClick={() =>
										navigate(`/treasury-manage/wish-list/info/${wishId}/add`)
									}
								>
									{t('button.add')}
								</Button>
							</Stack>
						</Box>
						<FormProvider {...methods}>
							<Box marginTop={2}>
								<AppDataGrid
									loading={!isGetPropertySummarySuccess}
									rows={transformResponse(propertySummary?.data?.items) ?? []}
									rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
									columns={columns}
									getRowId={(row) => row.id}
								/>
							</Box>
						</FormProvider>
					</CardContent>
				</Card>
			</Box>
		</>
	);
}
